/**
 * AttachmentList Component
 *
 * A standardized component for displaying file attachments/documents.
 * Supports click to open, optional delete, and various display styles.
 *
 * @example
 * ```jsx
 * // Basic usage
 * <AttachmentList
 *   attachments={documents}
 *   onDelete={(attachment) => handleDelete(attachment.id)}
 * />
 *
 * // Compact style for inline display
 * <AttachmentList
 *   attachments={task.attachments}
 *   variant="compact"
 * />
 *
 * // Read-only without delete
 * <AttachmentList
 *   attachments={files}
 *   readOnly
 * />
 * ```
 */

import {
	PaperClipIcon,
	DocumentIcon,
	PhotoIcon,
	DocumentTextIcon,
	XMarkIcon,
} from '@heroicons/react/20/solid';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

/**
 * Get appropriate icon based on file type/extension
 */
const getFileIcon = (attachment) => {
	const name = attachment?.name || attachment?.filename || '';
	const type = attachment?.type || attachment?.mimeType || '';
	const ext = name.split('.').pop()?.toLowerCase();

	// Image files
	if (
		type.startsWith('image/') ||
		['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)
	) {
		return PhotoIcon;
	}

	// PDF files
	if (type === 'application/pdf' || ext === 'pdf') {
		return DocumentTextIcon;
	}

	// Default document icon
	return DocumentIcon;
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes) => {
	if (!bytes) return null;
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Get the URL to open the attachment
 */
const getAttachmentUrl = (attachment) => {
	return attachment?.url || attachment?.location || attachment?.href || '#';
};

/**
 * Single attachment item component
 */
const AttachmentItem = ({
	attachment,
	onDelete,
	onClick,
	readOnly = false,
	variant = 'default',
	showSize = false,
}) => {
	const Icon = getFileIcon(attachment);
	const name = attachment?.name || attachment?.filename || 'Attachment';
	const size = formatFileSize(attachment?.size || attachment?.fileSize);
	const url = getAttachmentUrl(attachment);

	const handleClick = () => {
		if (onClick) {
			onClick(attachment);
		} else if (url && url !== '#') {
			window.open(url, '_blank', 'noopener,noreferrer');
		}
	};

	const handleDelete = (e) => {
		e.stopPropagation();
		if (onDelete) {
			onDelete(attachment);
		}
	};

	// Compact variant (for inline/chip display)
	if (variant === 'compact') {
		return (
			<div
				className={classNames(
					'inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded',
					'bg-gray-100 hover:bg-gray-200 text-gray-700 cursor-pointer',
					'transition-colors',
				)}
				onClick={handleClick}
			>
				<PaperClipIcon className="h-3 w-3 flex-shrink-0" />
				<span className="truncate max-w-[150px]">{name}</span>
				{!readOnly && onDelete && (
					<button
						type="button"
						onClick={handleDelete}
						className="ml-1 text-gray-400 hover:text-gray-600"
					>
						<XMarkIcon className="h-3 w-3" />
					</button>
				)}
			</div>
		);
	}

	// Default list item variant
	return (
		<li
			className={classNames(
				'flex items-center justify-between py-3 pl-3 pr-4 text-sm',
				'cursor-pointer hover:bg-gray-50 transition-colors',
			)}
			onClick={handleClick}
		>
			<div className="flex w-0 flex-1 items-center min-w-0">
				<Icon
					className="h-5 w-5 flex-shrink-0 text-gray-400"
					aria-hidden="true"
				/>
				<div className="ml-3 flex-1 min-w-0">
					<span className="truncate block text-xs font-medium text-gray-900">
						{name}
					</span>
					{showSize && size && (
						<span className="text-[10px] text-gray-500">{size}</span>
					)}
				</div>
			</div>
			{!readOnly && onDelete && (
				<button
					type="button"
					className="ml-4 flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
					onClick={handleDelete}
				>
					<XMarkIcon className="h-4 w-4" />
				</button>
			)}
		</li>
	);
};

/**
 * Main AttachmentList component
 */
const AttachmentList = ({
	attachments = [],
	onDelete,
	onClick,
	readOnly = false,
	variant = 'default', // 'default', 'compact'
	showSize = false,
	emptyText = 'No attachments',
	showEmpty = true,
	label,
	className = '',
}) => {
	const hasAttachments = attachments?.length > 0;

	// Compact variant - inline chips
	if (variant === 'compact') {
		if (!hasAttachments) {
			return showEmpty ? (
				<span className="text-[10px] text-gray-400">{emptyText}</span>
			) : null;
		}

		return (
			<div className={classNames('flex flex-wrap gap-2', className)}>
				{attachments.map((attachment, idx) => (
					<AttachmentItem
						key={attachment?.id || idx}
						attachment={attachment}
						onDelete={onDelete}
						onClick={onClick}
						readOnly={readOnly}
						variant="compact"
					/>
				))}
			</div>
		);
	}

	// Default list variant
	return (
		<div className={className}>
			{label && (
				<div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
					{label}
				</div>
			)}

			{hasAttachments ? (
				<div className="mt-3">
					<ul
						role="list"
						className="divide-y divide-gray-200 rounded-md border border-gray-200 mt-3"
					>
						{attachments.map((attachment, idx) => (
							<AttachmentItem
								key={attachment?.id || idx}
								attachment={attachment}
								onDelete={onDelete}
								onClick={onClick}
								readOnly={readOnly}
								variant="default"
								showSize={showSize}
							/>
						))}
					</ul>
				</div>
			) : showEmpty ? (
				<p className="text-sm text-gray-500 mt-3">{emptyText}</p>
			) : null}
		</div>
	);
};

/**
 * Attachment count badge - for showing attachment count inline
 */
export const AttachmentBadge = ({ count, onClick, className = '' }) => {
	if (!count || count === 0) return null;

	return (
		<span
			className={classNames(
				'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded',
				'bg-gray-100 text-gray-600',
				onClick && 'cursor-pointer hover:bg-gray-200',
				className,
			)}
			onClick={onClick}
		>
			<PaperClipIcon className="h-3 w-3" />
			{count} attachment{count !== 1 ? 's' : ''}
		</span>
	);
};

export { AttachmentItem };
export default AttachmentList;
